import { useCallback, useState } from 'react';
import { useAsyncData, useAsyncMutation } from './useAsyncData';
import { preferencesAPI, type UserPreferencesJson } from '@/services/api/preferences.api';
import type {
  OnboardingFlowState,
  WelcomeOnboarding,
  WelcomeOnboardingRoleKey,
} from '@/types/preferences.types';

interface UseWelcomeOnboardingResult {
  /** True una vez cargadas las preferences y el flag de este rol no está en `seen=true`. */
  shouldShow: boolean;
  isLoading: boolean;
  /**
   * Cierra el modal en esta sesión sin persistir nada. La próxima vez que el
   * usuario entre al área (recarga / login) volverá a aparecer.
   */
  dismissForNow: () => void;
  /**
   * Persiste `seen: true` en `User.preferences.welcomeOnboarding[roleKey]` y
   * marca el modal como completado. No vuelve a aparecer.
   */
  markCompleted: () => Promise<void>;
  isCompleting: boolean;
}

/**
 * Estado y persistencia del modal de bienvenida (welcome onboarding) por rol.
 *
 * El JSON `preferences` del usuario se mergea shallow en el backend (ver
 * PATCH /users/me/preferences). Para evitar pisar otros flags del mismo
 * `welcomeOnboarding`, hacemos GET previo y mandamos el objeto completo.
 *
 * @param roleKey - clave del rol (`'student'`, `'companyAdmin'`). Cada rol
 *                  tiene su propio flag para que cuando se monte el de empresa
 *                  no interfiera con el del alumno.
 */
export function useWelcomeOnboarding(roleKey: WelcomeOnboardingRoleKey): UseWelcomeOnboardingResult {
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  const prefs = useAsyncData<UserPreferencesJson>({
    fetchFn: () => preferencesAPI.get(),
  });

  const completeMutation = useAsyncMutation({
    mutationFn: async () => {
      const current = await preferencesAPI.get();
      const currentWelcome: WelcomeOnboarding = current.welcomeOnboarding ?? {};
      const flag: OnboardingFlowState = { status: 'completed', completedAt: Date.now() };
      const nextWelcome: WelcomeOnboarding = { ...currentWelcome, [roleKey]: flag };
      await preferencesAPI.patch({ welcomeOnboarding: nextWelcome });
    },
    onSuccess: () => {
      void prefs.refetch();
    },
  });

  const dismissForNow = useCallback(() => {
    setDismissedThisSession(true);
  }, []);

  const markCompleted = useCallback(async () => {
    await completeMutation.mutate();
  }, [completeMutation]);

  // Lectura retrocompatible: el esquema nuevo usa `status: 'completed'`; el
  // viejo usaba `seen: true`. Aceptamos ambos para no romper a usuarios que
  // completaron el welcome antes de la unificación.
  const flow = prefs.data?.welcomeOnboarding?.[roleKey] as
    | (OnboardingFlowState & { seen?: boolean })
    | undefined;
  const seen = flow?.status === 'completed' || flow?.seen === true;
  const shouldShow = !prefs.isLoading && !prefs.error && !seen && !dismissedThisSession;

  return {
    shouldShow,
    isLoading: prefs.isLoading,
    dismissForNow,
    markCompleted,
    isCompleting: completeMutation.isLoading,
  };
}
